// src/layouts/pages/orders/order-list/index.jsx
import React, { useCallback, useState, useEffect, useRef, useMemo, useContext } from 'react';
import PropTypes from 'prop-types';
import Card from '@mui/material/Card';
import SoftBox from 'components/SoftBox';
import DashboardLayout from 'prefabsComponents/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'prefabsComponents/Navbars/DashboardNavbar';
import Footer from 'prefabsComponents/Footer';
import DataTable from 'prefabsComponents/Tables/DataTable';
import { useNavigate } from 'react-router-dom';
import fetchData from 'layouts/pages/orders/order-list/data/dataTableData';
import AccionesCell from 'layouts/pages/orders/order-list/components/AccionesCell';
import ModalInfo from './components/ModalInfo';
import ModalEdit from './components/ModalEdit';
import DataTableLoader from 'components/DataTableLoader';
import DataTableHeaderOptions from 'components/DataTableHeaderOptions';
import ModalFiltros from './components/ModalFiltros';
import axios from 'axios';

// 👇 importa tu AuthContext
import { AuthContext } from 'context/AuthContext';

const STORAGE_KEY_FILTERS = "orderPageFilters";
const STORAGE_KEY_DATATABLE_SEARCH = "orderPageDatatableSearch";

const OrderList = () => {
  // 👇 obtiene rol/permiso
  const { hasRole } = useContext(AuthContext);
  // Solo admin y user pueden crear
  const canCreateOrder = hasRole('admin', 'user');

  // Estado central para los filtros
  const [appliedFilters, setAppliedFilters] = useState({});
  const currentPageData = useRef([]);
  const [dataTableData, setDataTableData] = useState({ columns: [], rows: [] });
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showModalInfo, setShowModalInfo] = useState(false);
  const [showModalEdit, setShowModalEdit] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);

  const navigate = useNavigate();

  const fetchOrders = useCallback(async ({ filters, search } = {}) => {
    setIsLoading(true);
    try {
      const queryParams = new URLSearchParams();
      if (search && search.trim() !== '') {
        queryParams.append('search', search.trim());
      } else if (filters) {
        if (filters.cliente) queryParams.append('cliente', filters.cliente.label);
        if (filters.incoterms) queryParams.append('incoterms', filters.incoterms.value);
        if (filters.fechaDesde) queryParams.append('fechaDesde', filters.fechaDesde.toISOString().split('T')[0]);
        if (filters.fechaHasta) queryParams.append('fechaHasta', filters.fechaHasta.toISOString().split('T')[0]);
        if (filters.estado) queryParams.append('estado', filters.estado.value);
        if (filters.numeroOrden && filters.numeroOrden.toString().trim() !== '')
          queryParams.append('numeroOrden', filters.numeroOrden);
      }
      const url = `${process.env.REACT_APP_SERVER_URL}/api/orders/?${queryParams.toString()}`;
      const response = await axios.get(url);
      const orders = response.data;
      const rows = orders.map(order => ({
        id: order.id_orden_cabecera,
        numero_orden_cabecera: order.numero_orden_cabecera,
        nombre_estado: order.nombre_estado,
        id_estado: order.id_estado,
        cliente: [order.cliente, { image: order.cliente.charAt(0) }],
        origen: order.origen,
        incoterms: order.incoterms,
        fechaEntrega: order.fecha_entrega,
        fechaRealEntrega: order.fecha_real,
        status: order.status,
      }));
      setDataTableData(prev => ({
        columns: prev.columns,
        rows,
      }));
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleFiltersUpdate = useCallback((filters) => {
    setAppliedFilters(filters);
    if (searchQuery && searchQuery.trim() !== '') {
      fetchOrders({ search: searchQuery });
    } else {
      fetchOrders({ filters });
    }
  }, [searchQuery, fetchOrders]);

  const getData = async () => {
    setIsLoading(true);
    try {
      const data = await fetchData();
      setDataTableData(data);
    } catch (error) {
      console.error('Error loading initial data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const loadInitialData = async () => {
      await getData();
      const savedFilters = localStorage.getItem(STORAGE_KEY_FILTERS);
      const savedSearch = localStorage.getItem(STORAGE_KEY_DATATABLE_SEARCH);
      if (savedSearch && savedSearch.trim() !== '') {
        setSearchQuery(savedSearch);
        await fetchOrders({ search: savedSearch });
      } else if (savedFilters) {
        try {
          const filters = JSON.parse(savedFilters);
          setAppliedFilters(filters);
          await fetchOrders({ filters });
        } catch (e) {
          console.error('Error parsing saved filters', e);
          await getData();
        }
      }
    };
    loadInitialData();
  }, []);

  const handleNewOrderClick = () => {
    navigate('/ordenes/nueva');
  };

  const handleShowModalInfo = (numeroOrdenCabecera) => {
    setSelectedOrder(numeroOrdenCabecera);
    setShowModalInfo(true);
  };

  const handleShowModalEdit = (numeroOrdenCabecera) => {
    setSelectedOrder(numeroOrdenCabecera);
    setShowModalEdit(true);
  };

  const handleCloseModalInfo = () => {
    setShowModalInfo(false);
    setSelectedOrder(null);
  };

  const handleCloseModalEdit = () => {
    setShowModalEdit(false);
    setSelectedOrder(null);
  };

  // Modal “crea y redirige”
  const CreateNewOrderModal = ({ show, handleClose, getData }) => {
    useEffect(() => {
      if (show) {
        handleNewOrderClick();
        handleClose();
      }
    }, [show, handleClose]);
    return null;
  };

  CreateNewOrderModal.propTypes = {
    show: PropTypes.bool.isRequired,
    handleClose: PropTypes.func.isRequired,
    getData: PropTypes.func.isRequired,
  };

  const table = useMemo(() => ({
    ...dataTableData,
    rows: dataTableData.rows.map(row => ({
      ...row,
      acciones: (
        <AccionesCell
          numeroOrdenCabecera={row.id}
          nombreEstado={row.nombre_estado}
          onShowModalInfo={handleShowModalInfo}
          onShowModalEdit={handleShowModalEdit}
          refreshTable={getData}
        />
      ),
    })),
  }), [dataTableData]);

  return (
    <DashboardLayout>
      <DashboardNavbar />

      {/* ⬇️ Si no puede crear, NO pasamos CreateNewModal/createNewTitle y el botón no aparece */}
      <DataTableHeaderOptions
        dataTableData={dataTableData}
        FilterModal={ModalFiltros}
        getData={getData}
        {...(canCreateOrder ? {
          CreateNewModal: CreateNewOrderModal,
          createNewTitle: "Nueva Orden",
        } : {})}
        currentPageData={currentPageData}
        onApplyFilters={async (filters) => {
          localStorage.setItem(STORAGE_KEY_FILTERS, JSON.stringify(filters));
          setAppliedFilters(filters);
          if (searchQuery && searchQuery.trim() !== '') {
            await fetchOrders({ search: searchQuery });
          } else {
            await fetchOrders({ filters });
          }
        }}
        initialFilters={appliedFilters}
      />

      <SoftBox my={3}>
        <Card>
          {isLoading ? (
            <DataTableLoader />
          ) : (
            <DataTable
              onPageData={(data) => { currentPageData.current = data; }}
              canSearch={false}
              entriesPerPage={true}
              showTotalEntries={true}
              table={table}
              defaultSearchValue={searchQuery}
              onSearchChange={(value) => {
                setSearchQuery(value);
                localStorage.setItem(STORAGE_KEY_DATATABLE_SEARCH, value);
                if (value.trim() !== '') {
                  fetchOrders({ search: value });
                } else {
                  const savedFilters = localStorage.getItem(STORAGE_KEY_FILTERS);
                  if (savedFilters) {
                    try {
                      const filters = JSON.parse(savedFilters);
                      fetchOrders({ filters });
                    } catch (e) {
                      getData();
                    }
                  } else {
                    getData();
                  }
                }
              }}
              onFiltersUpdate={handleFiltersUpdate}
            />
          )}
        </Card>
      </SoftBox>

      <Footer />

      <ModalInfo
        show={showModalInfo}
        handleClose={handleCloseModalInfo}
        numeroOrdenCabecera={selectedOrder}
      />

      <ModalEdit
        show={showModalEdit}
        handleClose={handleCloseModalEdit}
        numeroOrdenCabecera={selectedOrder}
        onRefresh={getData}
      />
    </DashboardLayout>
  );
};

export default OrderList;
